/**
 * UtmDashboard — Acquisition tracking + UTM link builder
 *
 * 3 sections:
 *   1. Link Builder — generate UTM URL from form
 *   2. Cohort Table — signups/activated/paid by source+campaign
 *   3. Recent Acquisitions Feed — last 20 users with UTM
 *
 * Phase A Bullseye decision support — measure CAC + activation per channel.
 */

import React, { useState, useEffect } from 'react';
import { Link2, Copy, Check, RefreshCw, Users, Award, ShoppingBag } from 'lucide-react';

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';
const BASE_URL = import.meta.env.VITE_LANDING_URL || 'https://dichtailieu.vn';

interface CohortRow {
  source: string;
  medium: string | null;
  campaign: string | null;
  signups: number;
  activated: number;
  paid: number;
  conversion_pct: number;
}

interface RecentRow {
  user_id: string;
  email: string | null;
  source: string;
  medium: string | null;
  campaign: string | null;
  signed_up_at: string;
  has_activated: boolean;
  has_paid: boolean;
}

interface CohortResponse {
  cohort: CohortRow[];
  recent: RecentRow[];
  total_signups: number;
  since: string;
  days: number;
}

const SOURCE_PRESETS = ['zalo', 'linkedin', 'facebook', 'email', 'blog', 'warm', 'youtube', 'google', 'cafef', 'vnexpress'];
const MEDIUM_PRESETS = ['group', 'dm', 'post', 'ad', 'reply', 'comment', 'organic', 'cpc', 'referral'];

const UtmDashboard: React.FC<{ token: string | null }> = ({ token }) => {
  // ── Link Builder state ──
  const [source, setSource] = useState('zalo');
  const [medium, setMedium] = useState('group');
  const [campaign, setCampaign] = useState('ai-share-vn');
  const [content, setContent] = useState('');
  const [copied, setCopied] = useState(false);

  // ── Cohort state ──
  const [cohort, setCohort] = useState<CohortRow[]>([]);
  const [recent, setRecent] = useState<RecentRow[]>([]);
  const [totalSignups, setTotalSignups] = useState(0);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build URL
  const buildUrl = (): string => {
    const params = new URLSearchParams();
    params.set('utm_source', source.trim());
    if (medium.trim()) params.set('utm_medium', medium.trim());
    if (campaign.trim()) params.set('utm_campaign', campaign.trim());
    if (content.trim()) params.set('utm_content', content.trim());
    return `${BASE_URL}?${params.toString()}`;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(buildUrl()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const fetchCohort = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/acquisition-cohort?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: CohortResponse = await res.json();
      setCohort(data.cohort);
      setRecent(data.recent);
      setTotalSignups(data.total_signups);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load cohort');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCohort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, days]);

  return (
    <div className="space-y-6">
      {/* ── Section 1: Link Builder ── */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Link2 className="w-5 h-5 text-emerald-600" />
          <h3 className="font-bold text-slate-800">UTM Link Builder</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Source <span className="text-red-500">*</span></label>
            <input
              list="source-presets"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="zalo, linkedin, facebook..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <datalist id="source-presets">
              {SOURCE_PRESETS.map(s => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Medium</label>
            <input
              list="medium-presets"
              value={medium}
              onChange={(e) => setMedium(e.target.value)}
              placeholder="group, dm, post, ad..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <datalist id="medium-presets">
              {MEDIUM_PRESETS.map(m => <option key={m} value={m} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Campaign</label>
            <input
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              placeholder="ai-share-vn, big4-audit, fdi-hr..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Content (optional A/B variant)</label>
            <input
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="hook-pain, hook-story..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-2">
          <code className="flex-1 text-xs text-slate-700 break-all font-mono">{buildUrl()}</code>
          <button
            onClick={handleCopy}
            disabled={!source.trim()}
            className="shrink-0 px-3 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-all"
          >
            {copied ? <><Check className="w-3.5 h-3.5" /> Đã copy</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
          </button>
        </div>
      </section>

      {/* ── Section 2: Cohort Table ── */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            <h3 className="font-bold text-slate-800">Acquisition Cohort</h3>
            <span className="text-xs text-slate-500">({totalSignups} signups in {days} days)</span>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="px-2 py-1 text-xs border border-slate-300 rounded-lg"
            >
              <option value={7}>7 ngày</option>
              <option value={14}>14 ngày</option>
              <option value={30}>30 ngày</option>
              <option value={90}>90 ngày</option>
            </select>
            <button
              onClick={fetchCohort}
              disabled={loading}
              className="p-1.5 text-slate-500 hover:text-emerald-600 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {error && <div className="text-sm text-red-600 mb-3">Error: {error}</div>}

        {cohort.length === 0 && !loading ? (
          <div className="text-center py-8 text-slate-400 text-sm">
            Chưa có user nào với UTM trong {days} ngày qua. Share link UTM để bắt đầu track.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-bold text-slate-500 uppercase">
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">Campaign</th>
                  <th className="py-2 pr-3 text-right">Signups</th>
                  <th className="py-2 pr-3 text-right">Activated</th>
                  <th className="py-2 pr-3 text-right">Paid</th>
                  <th className="py-2 text-right">Conv %</th>
                </tr>
              </thead>
              <tbody>
                {cohort.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-3 font-bold text-slate-700">{row.source}</td>
                    <td className="py-2 pr-3 text-slate-600">{row.campaign}</td>
                    <td className="py-2 pr-3 text-right font-mono">{row.signups}</td>
                    <td className="py-2 pr-3 text-right font-mono text-blue-600">{row.activated}</td>
                    <td className="py-2 pr-3 text-right font-mono text-emerald-600 font-bold">{row.paid}</td>
                    <td className="py-2 text-right font-mono">
                      <span className={row.conversion_pct >= 5 ? 'text-emerald-600 font-bold' : row.conversion_pct >= 2 ? 'text-amber-600' : 'text-slate-400'}>
                        {row.conversion_pct.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Section 3: Recent Acquisitions Feed ── */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Award className="w-5 h-5 text-amber-600" />
          <h3 className="font-bold text-slate-800">Recent Acquisitions</h3>
          <span className="text-xs text-slate-500">(latest {recent.length})</span>
        </div>

        {recent.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm">Không có acquisition gần đây.</div>
        ) : (
          <div className="space-y-2">
            {recent.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-slate-700 truncate">{u.email || u.user_id.slice(0, 8) + '...'}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    <span className="font-bold text-slate-700">{u.source}</span>
                    {u.campaign && <span className="text-slate-400"> · {u.campaign}</span>}
                    {u.medium && <span className="text-slate-400"> · {u.medium}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-3">
                  {u.has_activated && <span title="Activated" className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">A</span>}
                  {u.has_paid && <span title="Paid" className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded flex items-center gap-1"><ShoppingBag className="w-2.5 h-2.5" /> P</span>}
                  <span className="text-[10px] text-slate-400">
                    {new Date(u.signed_up_at).toLocaleDateString('vi-VN')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <p className="text-xs text-slate-400 text-center">
        UTM tracking per <code className="bg-slate-100 px-1 rounded">DEC-BIZ-2026-04-30-01</code>. First-touch attribution. PostHog event-level + Supabase user-level.
      </p>
    </div>
  );
};

export default UtmDashboard;
